import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQS = [
  {
    q: "How much does it cost to build a house in India in 2026?",
    a: "As a thumb rule, house construction cost in India ranges from about ₹1,650 per sq.ft for standard quality to ₹2,050 for premium and ₹2,750+ per sq.ft for luxury finishes. The final cost depends on your city, number of floors, foundation type, flooring and add-ons like modular kitchen or false ceiling. Use the calculator above for a personalised estimate.",
  },
  {
    q: "How much cement, steel and sand is needed per square foot?",
    a: "For an RCC-framed home, thumb rules are roughly 0.4 bags of cement, 4 kg of steel (TMT), 1.2 cu.ft of sand, 1.35 cu.ft of aggregate and about 8 bricks per sq.ft of built-up area. Premium and luxury builds use slightly more material per sq.ft.",
  },
  {
    q: "What is ground coverage and how does it affect built-up area?",
    a: "Ground coverage is the percentage of your plot that can be built upon after leaving mandatory setbacks. For example, a 30×40 (1,200 sq.ft) plot at 65% coverage gives roughly 780 sq.ft of built-up area per floor. Set your coverage in the calculator to get a realistic per-floor area.",
  },
  {
    q: "Am I eligible for a PMAY subsidy on home construction?",
    a: "Under PMAY (Pradhan Mantri Awas Yojana) Credit Linked Subsidy Scheme, EWS/LIG families (income up to ₹6 lakh) can get up to ₹2.67 lakh interest subsidy, MIG-I (up to ₹12 lakh) up to ₹2.35 lakh, and MIG-II (up to ₹18 lakh) up to ₹2.30 lakh — subject to carpet-area limits and being a first pucca house. Use the PMAY Subsidy Check above for an indicative amount.",
  },
  {
    q: "Does this calculator work for all Indian cities?",
    a: "Yes. It applies city-wise cost multipliers for metros like Mumbai, Delhi NCR, Bengaluru, Chennai, Hyderabad, Pune and Kolkata, as well as Tier-2 and Tier-3 cities. You can also compare the same build across cities and quality tiers.",
  },
];

export const SeoContent = () => (
  <section
    className="border-t border-slate-800 bg-slate-950/60 no-print"
    data-testid="seo-content"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white">
            Construction Cost Calculator for India
          </h2>
          <div className="mt-5 space-y-4 text-sm sm:text-base text-slate-400 leading-relaxed">
            <p>
              Planning to build your dream home? The{" "}
              <strong className="text-slate-200">
                NirmanDwar<sup className="text-[0.6em] text-orange-400">™</sup> Construction Cost Calculator
              </strong>{" "}
              gives you an accurate, city-wise estimate of your total construction cost and the exact
              quantities of cement, steel (TMT), sand, aggregate and bricks you will need — based on
              standard Indian IS Code and CPWD thumb rules for 2026.
            </p>
            <p>
              Enter your built-up area or pick a common plot size (like 30×40 or 40×60), choose your
              construction quality, city, foundation and finishes, and instantly see a detailed cost
              breakdown, material quantities, a city &amp; quality comparison, your construction loan
              EMI and your likely PMAY subsidy.
            </p>
            <p>
              Once you know your material requirements, compare live prices from{" "}
              <a
                href="https://nirmandwar.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-400 hover:text-orange-300 font-medium"
              >
                nirmandwar.com
              </a>{" "}
              — with 1,700+ verified local suppliers of cement, TMT steel, paint and bricks across
              India — and get the best price for your build.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              ["₹1,650+", "per sq.ft (standard)"],
              ["0.4 bags", "cement / sq.ft"],
              ["4 kg", "steel / sq.ft"],
              ["14+ cities", "cost multipliers"],
              ["Up to ₹2.67 L", "PMAY subsidy"],
              ["100% free", "no login needed"],
            ].map(([big, small]) => (
              <div key={small} className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
                <p className="font-heading text-lg font-extrabold text-orange-400 font-mono-data">
                  {big}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">{small}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-5">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border-slate-800"
                data-testid={`faq-${i}`}
              >
                <AccordionTrigger className="text-left text-slate-200 hover:text-white hover:no-underline text-sm sm:text-base font-semibold">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 text-sm leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </div>
  </section>
);
